/**
 * Node utility functions for ComfyUI workflow management
 */

/**
 * Get a human-readable display name for a node
 * @param {string} nodeId - The node ID
 * @param {Object} node - The node object containing class_type and other properties
 * @returns {string} - A formatted display name
 */
export const getNodeDisplayName = (nodeId, node) => {
  if (!node || !node.class_type) {
    return `Unknown Node (${nodeId})`;
  }

  // Convert class_type from camelCase/PascalCase to readable format
  const className = node.class_type
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();

  return `${className} (${nodeId})`;
};

/**
 * Get node type category for grouping and organization
 * @param {Object} node - The node object
 * @returns {string} - Node category
 */
export const getNodeCategory = (node) => {
  if (!node || !node.class_type) {
    return 'unknown';
  }

  const classType = node.class_type.toLowerCase();
  
  if (classType.includes('sampler') || classType.includes('ksampler')) {
    return 'sampler';
  }
  if (classType.includes('load') || classType.includes('input')) {
    return 'input';
  }
  if (classType.includes('save') || classType.includes('output') || classType.includes('preview')) {
    return 'output';
  }
  if (classType.includes('model') || classType.includes('checkpoint')) {
    return 'model';
  }
  if (classType.includes('vae')) {
    return 'vae';
  }
  if (classType.includes('clip') || classType.includes('text')) {
    return 'text';
  }
  if (classType.includes('image') || classType.includes('img')) {
    return 'image';
  }
  
  return 'other';
};

/**
 * Check if a node is an output node (generates images)
 * @param {Object} node - The node object
 * @returns {boolean} - True if it's an output node
 */
export const isOutputNode = (node) => {
  if (!node) {
    return false;
  }

  // First check for OUTPUT meta tag
  if (node._meta?.title === 'OUTPUT') {
    return true;
  }
  
  // Then check for OUTPUT in parameters
  if (node.inputs && Object.values(node.inputs).some(value => 
    typeof value === 'string' && value.includes('OUTPUT')
  )) {
    return true;
  }
  
  // Fallback to class type check
  if (!node.class_type) {
    return false;
  }

  const classType = node.class_type.toLowerCase();
  return classType.includes('save') || 
         classType.includes('output') || 
         classType.includes('preview') ||
         classType.includes('display');
};

/**
 * Check if a node is an input node (loads images)
 * @param {Object} node - The node object
 * @returns {boolean} - True if it's an input node
 */
export const isInputNode = (node) => {
  if (!node) {
    return false;
  }

  // First check for INPUT meta tag
  if (node._meta?.title === 'INPUT') {
    return true;
  }
  
  // Then check for INPUT in parameters
  if (node.inputs && Object.values(node.inputs).some(value => 
    typeof value === 'string' && value.includes('INPUT')
  )) {
    return true;
  }
  
  // Fallback to class type check
  if (!node.class_type) {
    return false;
  }

  const classType = node.class_type.toLowerCase();
  return classType.includes('load') || 
         classType.includes('input') ||
         classType.includes('upload');
};

/**
 * Get parameter type for a given parameter name
 * @param {string} paramName - The parameter name
 * @returns {string} - Parameter type (number, string, boolean, etc.)
 */
export const getParameterType = (paramName) => {
  const name = paramName.toLowerCase();
  
  if (name.includes('seed')) return 'number';
  if (name.includes('steps')) return 'number';
  if (name.includes('cfg')) return 'number';
  if (name.includes('width') || name.includes('height')) return 'number';
  if (name.includes('text') || name.includes('prompt')) return 'string';
  if (name.includes('enable') || name.includes('use')) return 'boolean';
  
  return 'string'; // Default to string
};

/**
 * Get parameter display name
 * @param {string} paramName - The parameter name
 * @returns {string} - Human-readable parameter name
 */
export const getParameterDisplayName = (paramName) => {
  return paramName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
};
